import { useNavigationStore } from '@store/NavigationStore';

import { Button, Dialog, Icon, Link, Text } from '@ui-component-library';
import { Accordion } from '@ui-component-library';

import { AlertDialogProps } from './AlertDialog.types';

export const AlertDialog = ({
  title,
  subtitle,
  designation,
  content,
  helperInfo,
  isOpen,
  onClose,
  onPrimaryAction,
  primaryActionText,
  size = 'small'
}: AlertDialogProps) => {
  const { setView } = useNavigationStore();

  const handleReadMoreClick = () => {
    setView({ comp: 'pricing' });
    if (history.state?.comp !== 'pricing') {
      history.pushState({ comp: 'pricing' }, '', '');
    }
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} size={size} className="z-[1070]">
      <Dialog.Header>
        <div className="rounded-full bg-[#EAAB11]/10 p-3">
          <Icon
            tw
            name="InfoOutlined"
            className="rounded-full bg-[##CC9000]/10 fill-[#CC9000]"
            size="xlarge"
          />
        </div>
        <Button tw variant="tertiary" size="icon" onClick={onClose}>
          <Icon tw name="CrossSimple" className="color-charcoal-500" size="large" />
        </Button>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-2">
        <Text variant="text-base/leading-normal/medium">{title}</Text>

        {content && (
          <div className="flex flex-col gap-4">
            <Text variant="text-sm/leading-normal/normal">{subtitle}</Text>
            <Text variant="text-sm/leading-normal/medium">Om du fortsätter:</Text>

            {content.accordions?.map((accordion, index) => (
              <Accordion key={index} isOpen={false} underline={false} className="pb-0 pl-2">
                <Accordion.Header>
                  <Text variant="text-sm/leading-normal/normal" className="text-[#1A1A1A]">
                    {accordion.label}
                  </Text>
                </Accordion.Header>
                <Accordion.Content>
                  <ul className="list-inside list-disc space-y-1">
                    {accordion.items.map((item, itemIndex) => (
                      <Text
                        variant="text-sm/leading-normal/normal"
                        key={itemIndex}
                        as="li"
                        className="text-[#737373] capitalize"
                      >
                        {item.toLowerCase()}
                      </Text>
                    ))}
                  </ul>
                </Accordion.Content>
              </Accordion>
            ))}

            <div className="mt-4 mb-2 flex flex-row items-center justify-between gap-3">
              <div className="flex flex-col gap-1">
                <Text variant="text-sm/leading-normal/medium">
                  Vill du visa flera fastigheter samtidigt?
                </Text>
                <Text variant="text-xs/leading-normal/normal">
                  Skaffa <span className="text-[#6856C3]">Treebula Plus</span>
                </Text>
              </div>
              <Button tw variant="secondary" size="small" onClick={handleReadMoreClick}>
                Läs mer
              </Button>
            </div>
          </div>
        )}

        {!content && subtitle && (
          <div className="flex flex-col gap-6">
            <Text variant="text-sm/leading-normal/normal">{subtitle}</Text>
            <Text variant="text-sm/leading-normal/normal" className="font-medium capitalize">
              {designation?.toLowerCase()}
            </Text>
          </div>
        )}

        {helperInfo && (
          <div className="mt-6 flex flex-col rounded-md border border-solid border-[#E6E6E6] bg-[#F5F5F5] p-3">
            <Text variant="text-xs/leading-normal/medium" className="mb-1">
              Behöver du hjälp?
            </Text>

            <Text variant="text-xs/leading-normal/normal" className="mb-3 text-[#737373]">
              {helperInfo}
            </Text>

            <div className="flex flex-row gap-3">
              <Link
                tw
                href="mailto:info@treebula.se"
                className="text-medium font-nista-grotesk text-xs"
              >
                info@treebula.se
              </Link>
              <Link tw href="tel:08-33 99 44" className="text-medium font-nista-grotesk text-xs">
                08-33 99 44
              </Link>
            </div>
          </div>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <Button tw fullWidth variant={onPrimaryAction ? 'tertiary' : 'primary'} onClick={onClose}>
          {onPrimaryAction ? 'Avbryt' : 'Stäng'}
        </Button>
        {onPrimaryAction && (
          <Button tw fullWidth variant="primary" onClick={onPrimaryAction}>
            {primaryActionText}
          </Button>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};
